import { useRef, useCallback } from 'react'
import gsap from 'gsap'

const SEGMENTS = 12

export default function useSpinWheel({ wheelRef, wheelRef2, pointerRef }) {
  const spinTweenRef = useRef(null)

  const lastRotation = useRef(0)
  const lastTime = useRef(performance.now())
  const lastStepIndex = useRef(0)
  const lastKickTime = useRef(0)

  const STEP = 360 / SEGMENTS

  const onUpdate = useCallback(() => {
    const now = performance.now()
    const rotation = gsap.getProperty(wheelRef.current, 'rotation')

    const deltaRotation = rotation - lastRotation.current
    const deltaTime = now - lastTime.current || 16

    lastRotation.current = rotation
    lastTime.current = now

    const velocity = Math.abs(deltaRotation / deltaTime) * 1000

    applyBlur(velocity)
    updatePointer(rotation, velocity)
  }, [])

  const applyBlur = useCallback(velocity => {
    const blur = gsap.utils.clamp(0, 8, velocity / 200)

    gsap.set([wheelRef.current, wheelRef2.current], {
      filter: blur > 0.3 ? `blur(${blur}px)` : 'none'
    })
  }, [])

  const updatePointer = useCallback((rotation, velocity) => {
    const angle = gsap.utils.wrap(0, 360, rotation)
    const stepIndex = Math.floor(angle / STEP)

    if (stepIndex !== lastStepIndex.current) {
      const stepDelta = stepIndex - lastStepIndex.current
      lastStepIndex.current = stepIndex

      // tick in both directions
      triggerPointerKick(velocity, stepDelta)
    }
  }, [])

  const triggerPointerKick = useCallback((velocity, stepDelta) => {
    const now = performance.now()
    if (now - lastKickTime.current < 28) return
    lastKickTime.current = now

    const direction = stepDelta > 0 ? -1 : 1
    const strength = gsap.utils.clamp(0.25, 0.1, velocity / 1000)

    gsap.to(pointerRef.current, {
      rotation: direction * strength * 30,
      duration: 0.05,
      ease: 'power2.out',
      transformOrigin: '50% 8%',
      overwrite: true,
      onComplete() {
        gsap.to(pointerRef.current, {
          rotation: 0,
          duration: 0.12,
          ease: 'elastic.out(1, 0.4)'
        })
      }
    })
  }, [])

  const startSpin = useCallback(() => {
    spinTweenRef.current?.kill()

    spinTweenRef.current = gsap.to([wheelRef.current, wheelRef2.current], {
      rotation: '+=360',
      duration: 1,
      ease: 'linear',
      repeat: -1,
      paused: true,
      onUpdate
    })

    gsap
      .timeline()
      .to([wheelRef.current, wheelRef2.current], {
        rotation: '-=30',
        duration: 1,
        ease: 'power2.out',
        onUpdate
      })
      .add(() => {
        spinTweenRef.current?.play()
      })
  }, [])

  const stopSpin = useCallback(onComplete => {
    spinTweenRef.current?.kill()
    spinTweenRef.current = null

    const EXTRA_ROTATIONS = 3
    const STOP_DURATION = 4.2
    gsap.to([wheelRef.current, wheelRef2.current], {
      rotation: `+=${360 * EXTRA_ROTATIONS}`,
      duration: STOP_DURATION,
      ease: 'power3.out', // smoother spin
      onUpdate,
      onComplete: () => {
        gsap.set([wheelRef.current, wheelRef2.current], {
          filter: 'none'
        })
        onComplete?.()
      }
    })
  }, [])

  return {
    startSpin,
    stopSpin
  }
}
